import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { BarChart3, Users, BookOpen, Award, TrendingUp } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { analyticsAPI } from '../../lib/api';
import { toast } from 'sonner';

const departments = ['All Departments', 'Engineering', 'Marketing', 'Sales', 'Human Resources', 'Finance', 'Operations', 'Product', 'Design'];

export default function AdminAnalyticsPage() {
  const [data, setData] = useState(null);
  const [deptData, setDeptData] = useState(null);
  const [selectedDept, setSelectedDept] = useState('All Departments');

  useEffect(() => {
    const fetchData = async () => {
      try { const res = await analyticsAPI.getDashboard(); setData(res.data); } catch { toast.error('Failed'); }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedDept !== 'All Departments') {
      const fetchDept = async () => {
        try { const res = await analyticsAPI.getDepartment(selectedDept); setDeptData(res.data); } catch { console.error('Failed'); }
      };
      fetchDept();
    } else { setDeptData(null); }
  }, [selectedDept]);

  if (!data) return <Layout><div className="flex justify-center py-8"><div className="spinner" /></div></Layout>;

  return (
    <Layout>
      <div className="space-y-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="font-sketch text-4xl">Analytics</h1>
            <p className="text-muted-foreground">Track learning progress</p>
          </div>
          <Select value={selectedDept} onValueChange={setSelectedDept}>
            <SelectTrigger className="input-sketchy w-48"><SelectValue /></SelectTrigger>
            <SelectContent>{departments.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
          </Select>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6">
          <div className="stats-card">
            <div className="flex items-center gap-3"><div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center"><Users className="w-6 h-6 text-blue-600" /></div><div><p className="text-sm text-muted-foreground">Users</p><p className="font-sketch text-3xl">{data.total_users}</p></div></div>
          </div>
          <div className="stats-card">
            <div className="flex items-center gap-3"><div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center"><BookOpen className="w-6 h-6 text-purple-600" /></div><div><p className="text-sm text-muted-foreground">Courses</p><p className="font-sketch text-3xl">{data.total_courses}</p></div></div>
          </div>
          <div className="stats-card">
            <div className="flex items-center gap-3"><div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center"><TrendingUp className="w-6 h-6 text-yellow-600" /></div><div><p className="text-sm text-muted-foreground">Enrollments</p><p className="font-sketch text-3xl">{data.total_enrollments}</p></div></div>
          </div>
          <div className="stats-card">
            <div className="flex items-center gap-3"><div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center"><Award className="w-6 h-6 text-green-600" /></div><div><p className="text-sm text-muted-foreground">Certificates</p><p className="font-sketch text-3xl">{data.total_certificates}</p></div></div>
          </div>
          <div className="stats-card">
            <div className="flex items-center gap-3"><div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center"><BarChart3 className="w-6 h-6 text-red-600" /></div><div><p className="text-sm text-muted-foreground">Completion</p><p className="font-sketch text-3xl">{data.completion_rate}%</p></div></div>
          </div>
        </div>

        {data.top_courses?.length > 0 && (
          <div className="card-sketchy">
            <h3 className="font-sketch text-xl mb-4">Top Courses</h3>
            <div className="space-y-3">
              {data.top_courses.map((c, i) => (
                <div key={c.id} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                  <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center font-bold">{i + 1}</div>
                  <div className="flex-1"><p className="font-semibold">{c.title}</p><p className="text-sm text-muted-foreground">{c.category}</p></div>
                  <div className="text-right"><p className="font-bold text-primary">{c.enrolled_count}</p><p className="text-xs text-muted-foreground">enrolled</p></div>
                </div>
              ))}
            </div>
          </div>
        )}

        {deptData && (
          <div className="card-sketchy">
            <h3 className="font-sketch text-xl mb-4">{selectedDept} Overview</h3>
            <div className="grid sm:grid-cols-4 gap-4">
              <div className="p-4 bg-gray-50 rounded-lg border-2 border-black"><p className="text-sm text-muted-foreground">Employees</p><p className="font-sketch text-2xl">{deptData.total_employees}</p></div>
              <div className="p-4 bg-gray-50 rounded-lg border-2 border-black"><p className="text-sm text-muted-foreground">Enrollments</p><p className="font-sketch text-2xl">{deptData.total_enrollments}</p></div>
              <div className="p-4 bg-gray-50 rounded-lg border-2 border-black"><p className="text-sm text-muted-foreground">Completed</p><p className="font-sketch text-2xl">{deptData.completed_courses}</p></div>
              <div className="p-4 bg-gray-50 rounded-lg border-2 border-black"><p className="text-sm text-muted-foreground">Rate</p><p className="font-sketch text-2xl text-green-600">{deptData.completion_rate}%</p></div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
